// Custom types for API responses
interface ApiResponse<T> {
  data: T;
}

// Simple in-memory cache for shortIds
interface ShortIdCache {
  [userId: string]: {
    shortId: string;
    timestamp: number;
  };
}

const shortIdCache: ShortIdCache = {};
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Get the base URL - use localhost for development
const BASE_URL = import.meta.env.DEV 
  ? 'http://localhost:3001/api'
  : `${window.location.protocol}//${window.location.host}/api`;

// Function to get cached shortId
export const getCachedShortId = (userId: string): string | null => {
  const cached = shortIdCache[userId];
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.shortId;
  }
  return null;
};

// Function to set cached shortId
export const setCachedShortId = (userId: string, shortId: string): void => {
  shortIdCache[userId] = {
    shortId,
    timestamp: Date.now(),
  };
};

// Declare fetch function type
type FetchShortId = (userId: string) => Promise<string>;

// Function to get or create shortId with caching
export const getOrCreateShortId: FetchShortId = async (userId: string) => {
  try {
    // Check cache first
    const cached = getCachedShortId(userId);
    if (cached) {
      return cached;
    }

    // If not in cache, fetch from server
    const response = await api.post<{ shortId: string }>('/vcard/generate', { userId });
    if (!response.data.shortId) {
      throw new Error('No shortId returned from server');
    }
    
    const { shortId } = response.data;
    
    // Cache the result
    setCachedShortId(userId, shortId);
    
    return shortId;
  } catch (error) {
    console.error('Failed to get/create shortId:', error);
    throw error;
  }
};

export const api = {
  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${BASE_URL}${endpoint}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Something went wrong');
      }

      const data = await response.json();
      return { data };
    } catch (error: any) {
      console.error('API Error:', error);
      throw new Error(error.message || 'Something went wrong');
    }
  },

  async post<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    try {
      console.log('Making API request to:', `${BASE_URL}${endpoint}`);
      const response = await fetch(`${BASE_URL}${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Something went wrong');
      }

      const responseData = await response.json();
      return { data: responseData };
    } catch (error: any) {
      console.error('API Error:', error);
      throw new Error(error.message || 'Something went wrong');
    }
  },

  async getProfile<T>(profileId: string): Promise<ApiResponse<T>> {
    return this.get<T>(`/profiles/${profileId}`);
  }
};
